import {
  createContext,
  createElement,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { getNormalizedApiBase } from '@/utils/apiUrlHelper';
import { SessionManager } from '@/utils/sessionManager';

const API_BASE = getNormalizedApiBase();

interface GoogleAuthContextValue {
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<{ success: boolean; error?: string }>;
}

const GoogleAuthContext = createContext<GoogleAuthContextValue | undefined>(undefined);

interface GoogleAuthProviderProps {
  children: ReactNode;
}

export const GoogleAuthProvider = ({ children }: GoogleAuthProviderProps) => {
  const { setUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isProcessingOAuthRef = useRef(false);

  useEffect(() => {
    const hasOAuthParams = (() => {
      if (typeof window === 'undefined') {
        return false;
      }

      const currentUrl = new URL(window.location.href);
      const searchParams = ['code', 'state', 'access_token', 'error'];
      const hasSearchParam = searchParams.some(param => currentUrl.searchParams.has(param));
      const hash = currentUrl.hash || '';
      const hasHashToken = hash.includes('access_token') || hash.includes('refresh_token') || hash.includes('provider_token');
      return hasSearchParam || hasHashToken;
    })();

    if (hasOAuthParams) {
      setLoading(true);
      setError(null);
    }

    const clearOAuthParams = () => {
      try {
        const currentUrl = new URL(window.location.href);
        ['code', 'state', 'access_token', 'error', 'expires_in', 'refresh_token', 'token_type'].forEach(param => {
          currentUrl.searchParams.delete(param);
        });

        if (currentUrl.hash && (currentUrl.hash.includes('access_token') || currentUrl.hash.includes('refresh_token'))) {
          currentUrl.hash = '';
        }

        const sanitizedUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
        window.history.replaceState({}, document.title, sanitizedUrl || currentUrl.pathname);
      } catch (error) {
        console.warn('clearOAuthParams - failed to sanitize URL', error);
      }
    };

    const processOAuthSession = async (session: Session | null) => {
      if (!session?.user || !session.access_token) {
        return;
      }

      const existingBackendToken = SessionManager.getToken();
      if (existingBackendToken) {
        console.log('OAuth session detected but backend token already exists, skipping');
        return;
      }

      if (isProcessingOAuthRef.current) {
        console.log('OAuth session already being processed, skipping duplicate');
        return;
      }

      isProcessingOAuthRef.current = true;
      setLoading(true);
      setError(null);

      try {
        console.log('Processing OAuth session received from Supabase listener');
        const response = await fetch(`${API_BASE}/api/auth/oauth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            supabaseToken: session.access_token,
            email: session.user.email,
          }),
        });

        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          const message = payload?.message || payload?.error?.message || `Erro ${response.status}`;
          throw new Error(message);
        }

        if (!payload?.success || !payload?.data?.session) {
          const message = payload?.message || payload?.error?.message || 'Erro ao processar login Google';
          throw new Error(message);
        }

        // Save session using SessionManager
        SessionManager.saveSession(
          payload.data.user,
          payload.data.session.access_token,
          payload.data.session.refresh_token
        );

        // Update user context
        setUser(payload.data.user);

        clearOAuthParams();
        console.log('OAuth login successful via Supabase session exchange', {
          user: payload.data.user?.email,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erro ao processar login Google';
        console.error('processOAuthSession - error:', error);

        SessionManager.clearTokensDirectly();

        setError(message);
      } finally {
        isProcessingOAuthRef.current = false;
        setLoading(false);
      }
    };

    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        await processOAuthSession(session);
      })
      .catch(error => {
        console.error('Error retrieving Supabase session on init:', error);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Supabase auth state changed:', event, session?.user?.email);

      if (event === 'SIGNED_IN') {
        await processOAuthSession(session);
      }

      if (event === 'SIGNED_OUT') {
        isProcessingOAuthRef.current = false;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [setUser]);

  const signInWithGoogle = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      await supabase.auth.signOut();

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/login`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao conectar com Google';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, []);

  const value = useMemo<GoogleAuthContextValue>(
    () => ({
      loading,
      error,
      signInWithGoogle,
    }),
    [loading, error, signInWithGoogle]
  );

  return createElement(GoogleAuthContext.Provider, { value }, children);
};

export const useGoogleAuth = () => {
  const context = useContext(GoogleAuthContext);

  if (!context) {
    throw new Error('useGoogleAuth deve ser utilizado dentro de um GoogleAuthProvider');
  }

  return context;
};
